import { EMPTY_DAILY_SALE } from './billToDailySales';

function pick(row, ...keys) {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    const value = row[key];
    if (value != null && value !== '') return value;
  }
  return '';
}

function asItemArray(value) {
  if (Array.isArray(value)) return value.filter((row) => row && typeof row === 'object');
  if (value && typeof value === 'object') {
    const vals = Object.values(value);
    if (
      vals.length &&
      vals.every((row) => row && typeof row === 'object' && !Array.isArray(row))
    ) {
      return vals;
    }
  }
  return [];
}

function firstItemList(...candidates) {
  for (const value of candidates) {
    const list = asItemArray(value);
    if (list.length) return list;
  }
  return [];
}

function looksLikeLineItem(row) {
  if (!row || typeof row !== 'object') return false;
  return Boolean(
    pick(row, 'item_code', 'itemCode', 'qty_sold', 'quantity', 'qty') ||
      (row.product && typeof row.product === 'object') ||
      pick(row, 'gross_amount', 'grossAmount', 'rate')
  );
}

export function listDailySalesFromResponse(res) {
  if (Array.isArray(res)) return res;
  const nested = res?.data ?? res ?? [];
  if (Array.isArray(nested)) return nested;
  if (Array.isArray(nested?.data)) return nested.data;
  if (Array.isArray(nested?.daily_sales)) return nested.daily_sales;
  if (Array.isArray(res?.daily_sales)) return res.daily_sales;
  return [];
}

/** Normalize GET daily sale show response into one sale object. */
export function unwrapDailySale(res) {
  if (!res || typeof res !== 'object') return null;

  if (Array.isArray(res.data)) {
    const items = res.data.filter((row) => row && typeof row === 'object');
    const first = items[0] || {};
    return {
      ...first,
      id: first.daily_sale_id ?? first.sale_id ?? first.id,
      sale_items: items,
      daily_sale_items: items,
      items,
    };
  }

  const rootCandidate =
    res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res;
  const root =
    rootCandidate.data &&
    typeof rootCandidate.data === 'object' &&
    !Array.isArray(rootCandidate.data) &&
    (rootCandidate.data.id != null ||
      rootCandidate.data.reference_number != null ||
      rootCandidate.data.daily_sale_items != null ||
      rootCandidate.data.sale_items != null)
      ? rootCandidate.data
      : rootCandidate;

  const nestedSale =
    (root.daily_sale && typeof root.daily_sale === 'object' && !Array.isArray(root.daily_sale)
      ? root.daily_sale
      : null) ||
    (root.sale && typeof root.sale === 'object' && !Array.isArray(root.sale) ? root.sale : null) ||
    (res.daily_sale && typeof res.daily_sale === 'object' && !Array.isArray(res.daily_sale)
      ? res.daily_sale
      : null);

  const base = nestedSale || root;
  if (!base || typeof base !== 'object' || Array.isArray(base)) return null;

  let items = firstItemList(
    base.daily_sale_items,
    base.sale_items,
    base.daily_sale_line_items,
    base.line_items,
    base.daily_sales,
    root.daily_sale_items,
    root.sale_items,
    root.daily_sale_line_items,
    root.line_items,
    root.daily_sales,
    res.daily_sale_items,
    res.sale_items,
    // only treat `items` as line items when it is an array/object of rows, not a count
    Array.isArray(base.items) || (base.items && typeof base.items === 'object')
      ? base.items
      : null,
    Array.isArray(root.items) || (root.items && typeof root.items === 'object')
      ? root.items
      : null
  );

  // Singular association variants
  if (!items.length) {
    const singular =
      base.daily_sale_item ||
      base.sale_item ||
      root.daily_sale_item ||
      root.sale_item ||
      null;
    if (singular && typeof singular === 'object' && !Array.isArray(singular)) {
      items = [singular];
    }
  }

  // Show endpoint sometimes returns a single line-item shaped daily_sale.
  if (!items.length && looksLikeLineItem(base)) {
    items = [base];
  }

  return {
    ...root,
    ...base,
    sale_items: items,
    daily_sale_items: items,
    items,
  };
}

export function formatSaleDate(value) {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatSaleDateTime(value) {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const date = formatSaleDate(d);
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${date} ${time}`;
}

export function formatSaleAmount(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function mapSaleItem(item = {}) {
  const product = item.product && typeof item.product === 'object' ? item.product : {};
  const itemCode = String(
    pick(item, 'item_code', 'itemCode') ||
      pick(product, 'item_code', 'itemCode', 'code') ||
      ''
  ).trim();
  const productName = String(
    pick(item, 'product_name', 'item_name', 'itemName', 'name') ||
      pick(product, 'item_name', 'name', 'product_name') ||
      ''
  ).trim();
  return {
    id: item.id,
    productLabel:
      productName && itemCode
        ? `${productName} (${itemCode})`
        : productName || itemCode || '—',
    billType: pick(item, 'bill_type', 'billType') || '—',
    billNo: pick(item, 'bill_no', 'billNo') || '—',
    itemCode: itemCode || '—',
    qtySold: pick(item, 'qty_sold', 'quantity', 'qty') || '—',
    unit: pick(item, 'unit_name', 'unit') || pick(product, 'unit') || '—',
    rate: pick(item, 'rate') || '—',
    grossAmount: pick(item, 'gross_amount', 'grossAmount') || '—',
    discount: pick(item, 'discount_amount', 'discount', 'dis_amount') || '—',
    taxRate: pick(item, 'tax_rate', 'taxRate') || '—',
    taxAmount: pick(item, 'tax_amount', 'taxAmount') || '—',
    amount: pick(item, 'amount', 'sales_amount', 'net_amount', 'total_amount') || '—',
  };
}

export function saleItemsFromRow(row = {}) {
  let items = firstItemList(
    row.daily_sale_items,
    row.sale_items,
    row.daily_sale_line_items,
    row.line_items,
    row.daily_sales,
    Array.isArray(row.items) || (row.items && typeof row.items === 'object') ? row.items : null,
    row.products
  );
  if (!items.length && looksLikeLineItem(row)) items = [row];
  return items.map(mapSaleItem);
}

function sumItemAmounts(items) {
  let total = 0;
  let saw = false;
  (items || []).forEach((item) => {
    const n = Number(item?.amount);
    if (!Number.isFinite(n)) return;
    saw = true;
    total += n;
  });
  return saw ? total : '';
}

export function mapDailySaleRow(row = {}) {
  const items = saleItemsFromRow(row);
  const itemsCountRaw =
    row.items_count ?? row.itemsCount ?? row.sale_items_count ?? row.daily_sale_items_count;
  const itemsCountNum = Array.isArray(itemsCountRaw)
    ? itemsCountRaw.length
    : Number(itemsCountRaw);

  // Guest count must come from guest fields only — never from items count / line count.
  const guestCount = pick(
    row,
    'guest_count',
    'guestCount',
    'covers',
    'pax',
    'no_of_guests',
    'number_of_guests',
    'total_guest_count'
  );

  const amount =
    pick(
      row,
      'amount',
      'total_amount',
      'sales_amount',
      'net_amount',
      'grand_total',
      'total',
      'sale_amount'
    ) || sumItemAmounts(items);

  return {
    id: row.id,
    referenceNumber: pick(
      row,
      'reference_number',
      'referenceNumber',
      'ref_no',
      'reference',
      'bill_reference'
    ),
    billNo: pick(row, 'bill_no', 'billNo'),
    billType: pick(row, 'bill_type', 'billType'),
    billDate: pick(row, 'bill_date', 'billDate', 'date'),
    location:
      pick(row, 'location_name', 'locationName') ||
      (row.location && typeof row.location === 'object' ? row.location.name : '') ||
      pick(row, 'location', 'outlet', 'outlet_name') ||
      '',
    itemsCount: Number.isFinite(itemsCountNum) ? itemsCountNum : items.length,
    amount,
    guestCount,
    createdAt: pick(row, 'created_at', 'createdAt', 'created_on', 'inserted_at'),
    updatedAt: pick(row, 'updated_at', 'updatedAt', 'updated_on'),
    items,
  };
}

/** Prefer API detail fields; fill gaps from list navigation state. */
export function mergeDailySaleDetails(apiSale, previewSale) {
  if (!apiSale && !previewSale) return null;
  if (!apiSale) return previewSale;
  if (!previewSale) return apiSale;

  const apiItems = Array.isArray(apiSale.items) ? apiSale.items : [];
  const previewItems = Array.isArray(previewSale.items) ? previewSale.items : [];
  const items = apiItems.length ? apiItems : previewItems;

  const pickFilled = (apiVal, previewVal) =>
    apiVal != null && apiVal !== '' ? apiVal : previewVal;

  return {
    ...previewSale,
    ...apiSale,
    referenceNumber: pickFilled(apiSale.referenceNumber, previewSale.referenceNumber),
    billNo: pickFilled(apiSale.billNo, previewSale.billNo),
    billType: pickFilled(apiSale.billType, previewSale.billType),
    billDate: pickFilled(apiSale.billDate, previewSale.billDate),
    location: pickFilled(apiSale.location, previewSale.location),
    guestCount: pickFilled(apiSale.guestCount, previewSale.guestCount),
    amount: pickFilled(apiSale.amount, previewSale.amount),
    createdAt: pickFilled(apiSale.createdAt, previewSale.createdAt),
    updatedAt: pickFilled(apiSale.updatedAt, previewSale.updatedAt),
    itemsCount: items.length || apiSale.itemsCount || previewSale.itemsCount || 0,
    items,
  };
}

function formValue(value) {
  if (value == null || value === '—') return '';
  return String(value);
}

/** Map a normalized daily sale (and first line item) onto the add/edit form. */
export function dailySaleToForm(sale) {
  const item = Array.isArray(sale?.items) && sale.items[0] ? sale.items[0] : {};
  return {
    ...EMPTY_DAILY_SALE,
    billNo: formValue(sale?.billNo) || formValue(item.billNo),
    billType: formValue(sale?.billType) || formValue(item.billType),
    outlet: formValue(sale?.location),
    itemCode: formValue(item.itemCode),
    quantity: formValue(item.qtySold),
    taxAmount: formValue(item.taxAmount),
    taxRate: formValue(item.taxRate),
    discountAmount: formValue(item.discount),
    grossAmount: formValue(item.grossAmount),
    salesAmount: formValue(item.amount),
    rate: formValue(item.rate),
    unit: formValue(item.unit),
  };
}
